import { RunningWithErrorsOutlined as RunningWithErrorsIcon } from "@mui/icons-material";
import React from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { MissedOccurrence } from "@/hooks/useMissedOccurrences";
import OccurrencesTableBase, {
  buildOccurrenceStoreLabel,
  defaultOccurrenceCsvTime,
  defaultOccurrenceTimeCell,
  type OccurrenceColumn,
} from "@/pages/reports/shared/OccurrencesTableBase";
import { Store } from "@/services/features/StoreService";
import { formatDurationShort } from "@/utils/time";

interface MissedOccurrencesTableProps {
  data: MissedOccurrence[];
  loading: boolean;
  error: string | null;
  storeTimezones: Record<number, string>;
  onRequestImage?: (occurrence: MissedOccurrence) => void;
  stores?: Store[];
  loadingKey: string;
  displayedDayCount: number;
}

const MissedOccurrencesTable: React.FC<MissedOccurrencesTableProps> = ({
  data,
  loading,
  error,
  storeTimezones,
  onRequestImage,
  stores = [],
  loadingKey,
  displayedDayCount,
}) => {
  const { t, i18n } = useTranslation();

  const columns = React.useMemo<OccurrenceColumn<MissedOccurrence>[]>(
    () => [
      {
        id: "time",
        mobileRole: "summary",
        label: t("storeInsights.missedOccurrencesTable.columns.time"),
        sortKey: "createdAt",
        headerSx: { minWidth: 145 },
        cellSx: { minWidth: 145 },
        renderCell: defaultOccurrenceTimeCell,
        getCsvValue: defaultOccurrenceCsvTime,
      },
      {
        id: "type",
        mobileRole: "summary",
        label: t("storeInsights.missedOccurrencesTable.columns.type"),
        sortKey: "type",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.type,
        getCsvValue: (row) => row.type,
      },
      {
        id: "image",
        mobileRole: "thumbnail",
        label: t("storeInsights.missedOccurrencesTable.columns.image"),
        renderCell: (row, context) =>
          row.isAssociateRequest ? null : context.renderImageCell(row),
      },
      {
        id: "product",
        mobileRole: "identity",
        label: t("storeInsights.missedOccurrencesTable.columns.product"),
        sortKey: "productName",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.productName,
        getCsvValue: (row) => row.productName,
      },
      {
        id: "value",
        label: t("storeInsights.missedOccurrencesTable.columns.value"),
        sortKey: "value",
        cellClassName: "text-[#001F3F]",
        renderCell: (row, context) =>
          row.isAssociateRequest
            ? ""
            : row.value
              ? context.formatOccurrenceAmount(row.value, row.storeId)
              : "--",
        getCsvValue: (row, context) =>
          row.isAssociateRequest
            ? ""
            : row.value
              ? context.formatOccurrenceAmountCsv(row.value, row.storeId)
              : "--",
      },
      {
        id: "sku",
        label: t("storeInsights.missedOccurrencesTable.columns.sku"),
        sortKey: "sku",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.sku,
        getCsvValue: (row) => row.sku,
      },
      {
        id: "size",
        label: t("storeInsights.missedOccurrencesTable.columns.size"),
        sortKey: "size",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.size,
        getCsvValue: (row) => row.size,
      },
      {
        id: "color",
        label: t("storeInsights.missedOccurrencesTable.columns.color"),
        sortKey: "color",
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => row.color,
        getCsvValue: (row) => row.color,
      },
      {
        id: "store",
        label: t("storeInsights.missedOccurrencesTable.columns.store"),
        sortKey: "storeName",
        sortAccessor: (row) => `${row.storeName} ${row.storeNumber}`,
        cellClassName: "text-[#001F3F]",
        renderCell: (row) => buildOccurrenceStoreLabel(row.storeName, row.storeNumber),
        getCsvValue: (row) => buildOccurrenceStoreLabel(row.storeName, row.storeNumber),
      },
      {
        id: "searchTime",
        label: t("storeInsights.missedOccurrencesTable.columns.searchTime"),
        sortKey: "searchTimeSeconds",
        align: "right",
        cellClassName: "text-[#001F3F]",
        renderCell: (row, context) =>
          formatDurationShort(row.searchTimeSeconds, context.currentLanguage),
      },
    ],
    [t],
  );

  return (
    <OccurrencesTableBase
      resetKey={loadingKey}
      title={t("storeInsights.missedOccurrencesTable.title")}
      data={data}
      loading={loading}
      error={error}
      storeTimezones={storeTimezones}
      columns={columns}
      emptyMessage={t("storeInsights.missedOccurrencesTable.empty.noRequests")}
      emptyState={
        <AdminEmptyState
          icon={<RunningWithErrorsIcon sx={{ fontSize: 40 }} />}
          text={t("storeInsights.missedOccurrencesTable.empty.noRequests")}
        />
      }
      loadingCallout={
        <LongRunningDataLoader
          variant="table"
          isLoading={true}
          baseLabels={[
            t("storeInsights.common.loading.longRunningReport"),
            t("storeInsights.common.loading.readingActivity"),
            t("storeInsights.common.loading.calculatingInsights"),
            t("storeInsights.common.loading.preparingResults"),
          ]}
          loopingLabels={[
            t("storeInsights.common.loading.stillWorking"),
            t("storeInsights.common.loading.thanksForPatience"),
          ]}
          estimate={{
            meanTimePerStoreMs: 600,
            storeCount: stores.length,
            displayedDayCount,
            growth: "logarithmic",
            logarithmicLinearWeight: 0.1,
          }}
          locale={i18n.resolvedLanguage || i18n.language}
          loadingKey={loadingKey}
          estimateLabel={(duration) => t("storeComparison.loading.usuallyUnder", { duration })}
        />
      }
      onRequestImage={onRequestImage}
      stores={stores}
      noDecimalAmounts
      paginate
    />
  );
};

export default MissedOccurrencesTable;
